import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { History as HistoryIcon, Loader2, Search, X, Video, Film, FileDown, ShoppingBag, ExternalLink, Play, Trash2 } from "lucide-react";
import api, { imageUrl, API } from "@/lib/api";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function History() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null);
  const downloadPdf = async (e, m) => {
    e.stopPropagation();
    setPdfBusy(m.manual_id);
    try {
      const res = await api.get(`/manuals/${m.manual_id}/guide-pdf`, { responseType: "blob" });
      const slug = (m.title || "guide").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `stepmystep-${slug}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't download the PDF");
    } finally {
      setPdfBusy(null);
    }
  };
  const [query, setQuery] = useState("");

  const load = () => api.get("/history").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!items) return items;
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => (m.title || "").toLowerCase().includes(q) || (m.channel || "").toLowerCase().includes(q) || (m.category || "").toLowerCase().includes(q));
  }, [items, query]);

  const removeOne = async (e, id) => {
    e.stopPropagation();
    setItems((list) => list.filter((x) => x.manual_id !== id));
    try { await api.delete(`/history/${id}`); } catch { /* best-effort */ }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear your entire viewing history?")) return;
    setItems([]);
    try { await api.delete("/history"); toast.success("History cleared"); } catch { toast.error("Couldn't clear history"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 pt-8 pb-16">
      <div className="flex items-center justify-between mb-1 gap-3">
        <h1 className="text-3xl font-extrabold flex items-center gap-2.5 sms-outline" data-testid="history-title">
          <HistoryIcon size={26} className="text-white" /> History
          {items?.length > 0 && (
            <span data-testid="history-count-badge" className="text-sm font-bold text-[#5d8a52] bg-[#eef3ea] px-2.5 py-1 rounded-full tabular-nums">{items.length}</span>
          )}
        </h1>
        <div className="flex items-center gap-2">
          <button
            data-testid="go-myvideos-btn"
            onClick={() => navigate("/my-videos")}
            title="My Videos"
            className="text-[#5d8a52] font-bold px-3 py-2.5 rounded-xl flex items-center gap-1.5 bg-[#eef3ea] hover:bg-[#e0ecd9] transition-colors"
          >
            <Video size={18} /> <span className="hidden sm:inline text-sm"><span className="text-[#5d8a52]">My</span> Videos</span>
          </button>
          {items?.length > 0 && (
            <button data-testid="clear-history-btn" onClick={clearAll} className="text-[#9a96ac] hover:text-[#e0455c] font-semibold text-sm px-3 py-2.5 rounded-xl hover:bg-[#fdeef0] transition-colors">Clear all</button>
          )}
        </div>
      </div>
      <p className="text-sm text-[#e4f0e4] mb-5">Community videos you've viewed — newest first.</p>

      {items?.length > 0 && (
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5d8a52]" />
          <input
            data-testid="history-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your history…"
            className="w-full text-sm bg-black text-white placeholder:text-white/50 border-2 border-[#5d8a52] rounded-full pl-9 pr-9 py-2.5 outline-none focus:border-[#bcd2b0] transition-colors"
          />
          {query && (
            <button data-testid="history-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b7b3c8] hover:text-[#6b6880]"><X size={15} /></button>
          )}
        </div>
      )}

      {items === null ? (
        <div className="py-20 flex justify-center"><Loader2 size={28} className="spin text-[#5d8a52]" /></div>
      ) : items.length === 0 ? (
        <div className="sms-card p-10 text-center text-[#6b6880]">
          <p className="mb-4">No history yet. Videos you open in the Community will show up here.</p>
          <button onClick={() => navigate("/")} className="sms-gradient-btn text-white font-bold px-6 py-3 rounded-2xl">Browse Community</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="sms-card p-8 text-center text-[#6b6880]" data-testid="no-results">Nothing matches "{query}".</div>
      ) : (
        <div className="rounded-2xl border-2 border-[#5d8a52] sms-tweed overflow-hidden divide-y divide-white/10 text-white">
          {filtered.map((m) => (
            <div
              key={m.manual_id}
              data-testid={`history-row-${m.manual_id}`}
              onClick={() => navigate(`/community/${m.manual_id}`)}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition-colors cursor-pointer group"
            >
              {/* micro thumbnail */}
              <div className="w-14 h-10 rounded-md bg-[#17171f] overflow-hidden shrink-0 relative">
                {m.hero_image_id && <img src={imageUrl(m.hero_image_id)} alt="" className="w-full h-full object-cover" />}
                {m.has_video && (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity"><Play size={14} className="text-white" fill="#fff" /></span>
                )}
              </div>

              {/* title + meta */}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate leading-tight">{m.title}</div>
                <div className="flex items-center gap-1.5 text-xs text-[#9a96ac] truncate">
                  {m.channel && <span className="truncate max-w-[120px]">{m.channel}</span>}
                  {m.channel && m.category && <span>·</span>}
                  {m.category && <span className="truncate">{m.category}</span>}
                </div>
              </div>

              {/* links */}
              <div className="flex items-center gap-0.5 shrink-0">
                {m.has_video && (
                  <a
                    data-testid={`history-watch-${m.manual_id}`}
                    href={`${API}/community/${m.manual_id}/video/stream`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title="Watch video"
                    className="p-1.5 rounded-lg text-[#a9c39c] hover:text-[#5d8a52] hover:bg-[#eef3ea] transition-colors"
                  >
                    <Film size={16} />
                  </a>
                )}
                {m.has_pdf && (
                  <button
                    data-testid={`history-pdf-${m.manual_id}`}
                    onClick={(e) => downloadPdf(e, m)}
                    disabled={pdfBusy === m.manual_id}
                    title="Download manual (PDF)"
                    className="p-1.5 rounded-lg text-[#a9c39c] hover:text-[#5d8a52] hover:bg-[#eef3ea] transition-colors disabled:opacity-60"
                  >
                    {pdfBusy === m.manual_id ? <Loader2 size={16} className="spin" /> : <FileDown size={16} />}
                  </button>
                )}
                {m.amazon_url && (
                  <a
                    data-testid={`history-amazon-${m.manual_id}`}
                    href={m.amazon_url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    onClick={(e) => e.stopPropagation()}
                    title="Shop supplies (Amazon)"
                    className="p-1.5 rounded-lg text-[#a9c39c] hover:text-[#e59819] hover:bg-[#fff6e6] transition-colors"
                  >
                    <ShoppingBag size={16} />
                  </a>
                )}
                {m.source_url && (
                  <a
                    data-testid={`history-source-${m.manual_id}`}
                    href={m.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title="Original source"
                    className="p-1.5 rounded-lg text-[#a9c39c] hover:text-[#5d8a52] hover:bg-[#eef3ea] transition-colors"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
                <span className="text-[11px] text-[#b7b3c8] tabular-nums w-14 text-right hidden sm:inline">{timeAgo(m.viewed_at)}</span>
                <button
                  data-testid={`history-remove-${m.manual_id}`}
                  onClick={(e) => removeOne(e, m.manual_id)}
                  title="Remove from history"
                  aria-label="Remove from history"
                  className="p-1.5 rounded-lg text-[#cbc4e6] hover:text-[#e0455c] hover:bg-[#fdeef0] transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
